/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import {
  OnInit,
  Component,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionType, WorkflowAction } from './workflow-action-types/workflow-action';
import { SendEmailAction } from './workflow-action-types/send-email-action';
import { WorkflowService } from '../../../../../services/workflow.service';
import { WorkflowModel } from '../../../../../models/workflow.model';
import {
  ButtonAction,
  ButtonType,
  ConfirmDeleteDialogData,
  ConfirmDialogService
} from '@epg/ng-components';
import { faPlus, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import {
  UpdateWorkflowNotificationTrigger,
  WorkflowNotificationTrigger
} from '../../../../../models/workflowNotificationTrigger.model';
import { toTitleCase } from '../../../../../app/shared/functions';
import { Answer, IncidentQuestionData } from './send-email-action/send-email-action.component';

@Component({
  selector: 'oneapp-workflow-actions-management',
  templateUrl: './workflow-actions-management.component.html',
  styleUrl: './workflow-actions-management.component.scss',
  standalone: false
})
export class WorkflowActionsManagementComponent implements OnInit, OnChanges {
  @Input({ required: true }) workflow!: WorkflowModel;
  faPlus = faPlus;
  ActionType = ActionType;
  ButtonAction = ButtonAction;
  ButtonType = ButtonType;
  incidentQuestions: IncidentQuestionData[] = [];
  workflowIdInDesigner: string = '';
  loadingWorkflow = false;
  workflowNotificationTriggers: WorkflowNotificationTrigger[] = [];

  actions: WorkflowAction[] = [];
  faSpinnerThird = faSpinnerThird;

  unsavedChangesExist: boolean = false;
  @Output() unsavedChangesExistEvent = new EventEmitter<boolean>();

  actionsLoading: boolean = false;
  isDeleting: boolean = false;
  isActionsChanged: boolean = false;
  isActionsValid: boolean = false;
  constructor(
    private workflowService: WorkflowService,
    private readonly dialogService: ConfirmDialogService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.ensureStatesLoaded();
    await this.loadSendEmailActions();
    await this.updateTriggerRecipients();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const workflowChange = changes['workflow'];
    if (
      workflowChange &&
      !workflowChange.firstChange &&
      workflowChange.currentValue?.name !== workflowChange.previousValue?.name
    ) {
      this.actions = [];
      await this.ensureStatesLoaded();
      await this.loadSendEmailActions();
      await this.updateTriggerRecipients();
    }
  }

  private async ensureStatesLoaded(): Promise<void> {
    if (!this.workflow.states || this.workflow.states.length === 0) {
      if (this.workflow.combinedId) {
        const fullWorkflow = await firstValueFrom(
          this.workflowService.getCombinedWorkflowById(this.workflow.combinedId)
        );
        this.workflow.states = fullWorkflow.states;
      }
    }
  }

  async updateTriggerRecipients(): Promise<void> {
    this.loadingWorkflow = true;
    this.workflowNotificationTriggers = await this.workflowService.searchNotificationTrigger(
      this.workflow.name
    );
    this.workflowIdInDesigner =
      (await this.workflowService.workflowExistsInDesigner(this.workflow.name))?._id ?? '';

    this.readWorkflowIncidents();
    this.loadingWorkflow = false;
  }

  async loadSendEmailActions(): Promise<void> {
    if (this.actionsLoading) return;
    this.unsavedChangesExist = false;
    this.unsavedChangesExistEvent.emit(false);
    this.actionsLoading = true;
    this.actions = [];
    let notificationTriggers: WorkflowNotificationTrigger[] =
      await this.workflowService.searchNotificationTrigger(this.workflow.name);
    for (let trigger of notificationTriggers) {
      let action = new SendEmailAction();
      action.id = trigger._id;
      action.trigger_options = trigger.trigger_options;
      action.recipient_options = trigger.recipient_options;
      this.actions.push(action);
    }
    this.actionsLoading = false;
  }

  async onSubmit(): Promise<void> {
    this.unsavedChangesExist = false;
    this.unsavedChangesExistEvent.emit(false);

    for (let action of this.actions) {
      if (action.isChanged) {
        switch (action.type) {
          case ActionType.SendEmail:
            let notificationTrigger: UpdateWorkflowNotificationTrigger = {
              object_reference: this.workflow.name,
              trigger_options: (<SendEmailAction>action).trigger_options ?? {},
              recipient_options: (<SendEmailAction>action).recipient_options ?? {}
            };

            if ((<SendEmailAction>action).id) {
              await this.workflowService.updateNotificationTrigger(
                notificationTrigger,
                (<SendEmailAction>action).id!
              );
            } else {
              let createdTrigger =
                await this.workflowService.createNotificationTrigger(notificationTrigger);
              (<SendEmailAction>action).id = createdTrigger._id;
            }

            break;
          default:
            break;
        }
        action.isChanged = false;
      }
    }
    this.isActionsChanged = false;
    this.unsavedChangesExist = false;
    this.unsavedChangesExistEvent.emit(false);
  }
  onActionChanged(index: number) {
    this.actions[index].isChanged = true;
    this.isActionsChanged = true;
    this.unsavedChangesExist = true;
    this.unsavedChangesExistEvent.emit(true);

    this.isActionsValid = this.checkIsActionsValid();
  }
  async onDeleteAction(index: number): Promise<void> {
    const title = $localize`:Delete Email Notification for Task Protocols@@DeleteEmailNotificationForTaskProtocols:Delete Email Notification for Task Protocols`;
    const msg = $localize`:This action cannot be undone.@@ThisActionCannotBeUndone:This action cannot be undone.`;
    const confirmButtonText = $localize`:button text delete Notification@@buttonDeleteNotification:DELETE NOTIFICATION`;
    const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      title: title,
      message: msg,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe(async (result: boolean) => {
      if (result == true) {
        let action = this.actions.splice(index, 1)[0];

        switch (action.type) {
          case ActionType.SendEmail:
            if ((<SendEmailAction>action).id) {
              this.isDeleting = true;
              await this.workflowService.deleteNotificationTrigger((<SendEmailAction>action).id!);
              this.isDeleting = false;
            }
            this.isActionsValid = this.checkIsActionsValid();
            break;
          default:
            break;
        }
      }
    });
  }

  onAddProtocolNotification(): void {
    this.actions.push(new SendEmailAction());
  }

  readWorkflowIncidents() {
    this.incidentQuestions = [];

    for (let state of this.workflow.states as any[]) {
      if (state.notificationTriggers && state.notificationTriggers!.length > 0) {
        switch (state.workflowItem) {
          case 'QUESTION': {
            let question: IncidentQuestionData = {
              answers: []
            };
            let incidentEvents: string[] = [];
            question.questionText = state.properties.questionText.value;
            for (let notificationTrigger of state.notificationTriggers as any[]) {
              incidentEvents.push(notificationTrigger.value);
            }
            for (let answer of state.properties.answers.value as any[]) {
              let incidentAnswer: Answer = {};
              incidentAnswer.answerText = answer.value.displayText.value;
              incidentAnswer.isIncident = incidentEvents.includes(answer.value.event.value);
              incidentAnswer.triggerType = { state_id: state.id, data: answer.value.event };
              question.answers!.push(incidentAnswer);
            }
            this.incidentQuestions.push(question);
            break;
          }
        }
      }
    }
  }

  checkIsActionsValid(): boolean {
    return this.actions.every((action) => action.isValid());
  }
}
